// TriggerSet.js – versão completa (clássico + RTA)

import { ParseNumber, SimpleNumberFormat } from '../../../global';
import Trigger from './Trigger';
import TriggerTableSet from './TriggerTableSet';
import MediatorTrigger from "../tools/MediatorTrigger";

export default class TriggerSet {
  // propriedades “estáticas” por instância
  databaseTriggers = [];
  loadTriggerReliability = {};
  loadTriggerAverageValue = {};
  item = '';

  constructor(triggersInfo = []) {
    // triggersInfo pode vir como Array OU como dicionário { code: info }
    this.triggersInfo = triggersInfo;
    this.triggers = [];
    this.setNewTriggers();
  }

  /* =======================================================================
   * helpers
   * ===================================================================== */
  destructureKey(key) {
    if (!key) return { indicatorId: -9999, unitId: 0, system: '' };
    const [indicatorId, unitId, system] = key.split('-');
    return { indicatorId, unitId, system };
  }

  #infoList() {
    return Array.isArray(this.triggersInfo)
      ? this.triggersInfo
      : Object.values(this.triggersInfo || {});
  }
  #infoByCode(code) {
    if (Array.isArray(this.triggersInfo)) {
      return this.triggersInfo.find(t => t.triggercode === code);
    }
    return this.triggersInfo[code];
  }

  /* =======================================================================
   * cria “gatinhos” em branco
   * ===================================================================== */
  setNewTriggers(newConfig = {}) {
    // se já estamos em “modo novo” (id === 0) evita recriar do zero
    if (this.triggers.length && this.triggers[0].id === 0) return;
  
    // 1) pega os IDs que o usuário selecionou no formulário RTA
    const tp = MediatorTrigger.rtaTriggers?.triggerProductions || {};
  
    // 2) constrói newConfig com fallback em três níveis:
    //    a) valor recebido em newConfig
    //    b) valor atual em triggerProductions
    //    c) 0 (default)
    newConfig = {
      indicatorKey: newConfig.indicatorKey ?? '0-1-P',
      processId   : newConfig.processId    ?? tp.processId   ?? 0,
      productId   : newConfig.productId    ?? tp.productId   ?? 0,
      equipmentId : newConfig.equipmentId  ?? tp.equipmentId ?? 0,
    };
  
    // 3) destrincha indicatorKey e recria o array de gatilhos em branco
    this.triggers = [];
    const { indicatorId, unitId, system } = this.destructureKey(newConfig.indicatorKey);
  
    for (const info of this.#infoList()) {
      if (info.triggercode === 'MAN') continue; // ignora “manual”
      this.triggers.push(
        new Trigger({
          ...info,
          id: 0,                        // ainda não salvo
          triggerId: info.id,           // id da tabela info
          indicatorId,
          unitId,
          system,
          processId  : newConfig.processId,
          productId  : newConfig.productId,
          equipmentId: newConfig.equipmentId,
        })
      );
    }
  }

  /* =======================================================================
   * setters de informações gerais
   * ===================================================================== */
  setIndicator(obj, conf) {
    this.triggers.forEach(t => { t.setIndicator(obj); t.setReliability(conf); });
  }
  setIndicatorConf(conf) { this.triggers.forEach(t => t.setReliability(conf)); }

  #setCommon(obj, prop) {
    const idKey   = `${prop}Id`;
    const descKey = prop;
    const id      = obj ? obj.id          : 0;
    const desc    = obj ? obj.description : '';
    this.triggers.forEach(t => { t[idKey] = id; t[descKey] = desc; });
  }
  setProcess(obj)   { this.#setCommon(obj, 'process');   }
  setProduct(obj)   { this.#setCommon(obj, 'product');   }
  setEquipment(obj) { this.#setCommon(obj, 'equipment'); }

  /* =======================================================================
   * atualiza conteúdo de um gatilho específico
   * ===================================================================== */
  setContent(configurationJSON, triggercode) {
    // 1) já existe
    const trg = this.triggers.find(t => t.triggercode === triggercode || t.triggercode === `${triggercode}_RTA`);
    if (trg) {
      trg.active = Boolean(configurationJSON.active);
      delete configurationJSON.active;
      trg.setContent(configurationJSON);
      return;
    }

    // 2) novo
    if (!this.item) return;
    const [indicatorId, unitId, system, processId, productId, equipmentId] = this.item.key.split('-');
    const isActive = configurationJSON.hasOwnProperty('active') ? Boolean(configurationJSON.active) : false;
    delete configurationJSON.active;

    const info = this.#infoByCode(triggercode);

    const t = new Trigger({
      active: isActive,
      indicatorId,
      unitId,
      system,
      processId,
      productId,
      equipmentId,
      triggercode,
      triggerId: info ? info.id : 0,
    });

    t.setContent(configurationJSON);
    this.triggers.push(t);
  }

  /* =======================================================================
   * coleta de dados para salvar
   * ===================================================================== */
  getTriggersToSave() {
    return this.triggers
      .map(t => {
        // validação de VLV ativo
        if (t.triggercode === 'VLV' && t.active) {
          const cfg = JSON.parse(t.configurationJSON);
          if (cfg.minActive && cfg.maxActive && parseFloat(cfg.minValue) > parseFloat(cfg.maxValue))
            throw 'O valor mínimo não pode ser maior que o valor máximo';
        }
        if (t.active || t.configurationJSON !== '') {
          return {
            ...t,
            id:                 ParseNumber(t.id),
            indicatorId:        ParseNumber(t.indicatorId),
            unitId:             ParseNumber(t.unitId),
            triggerId:          ParseNumber(t.triggerId),
            processId:          ParseNumber(t.processId),
            equipmentId:        ParseNumber(t.equipmentId),
            productId:          ParseNumber(t.productId),
            active:             t.active,
            target:             SimpleNumberFormat(t.target !== '-' ? t.target : '-9999'),
            operationalRange:   SimpleNumberFormat(t.operationalRange),
            criticalRange:      SimpleNumberFormat(t.criticalRange),
            reliabilityCritical: SimpleNumberFormat(t.reliabilityCritical),
            reliabilityThreshold:SimpleNumberFormat(t.reliabilityThreshold),
            type: t.type,
          };
        }
        return null;
      })
      .filter(Boolean);
  }

  /* =======================================================================
   * integração com backend
   * ===================================================================== */
  setCollection(data) { this.databaseTriggers = data.map(d => new Trigger(d)); }

  canLoadTriggers(value) {
    const key = `${value.indicatorKey}-${value.processId}-${value.productId || 0}-${value.equipmentId || 0}`;
    const sel = this.databaseTriggers.filter(t => t.key === key);
    if (sel.length) {
      const tbl = new TriggerTableSet(sel);
      return this.getTriggers(tbl.collection[0]);
    }
    this.setNewTriggers(value);
  }

  getTriggers(item) {
    this.item = item;
    this.triggers = this.databaseTriggers.filter(t => t.key === item.key);
    return this.getMapTriggerTableLine(item);
  }

  getListTriggerTableLine(updated) {
    return [...this.getMapTriggerTableLine(updated).values()].filter(v => v instanceof Trigger);
  }

  /* =======================================================================
   * construção do Map para a linha da tabela
   * ===================================================================== */
  getMapTriggerTableLine(update) {
    const codes = [
      'VLV','VFR','VLT',
      'ALT','ALD','ATS','ADI','ATM',
      'VMD','VMT','AMH','VMH','VMW',
      'RFG','RFI','RSG'
    ];
    const map = new Map();
    for (const code of codes) {
      const trg = this.getTriggerFromTriggerTableLine(update.key, code) || {};
      trg.active = update[code.toLowerCase()] ?? false;
      map.set(code, trg);
    }
    return map;
  }

  getTriggerFromTriggerTableLine(key, triggercode) {
    const aliases = [triggercode, `${triggercode}_RTA`];
    return this.databaseTriggers.find(t => t.key === key && aliases.includes(t.triggercode));
  }
}
